/**
 * AiAdvisoryChip — drafts/54 §AI. A small card at the top of the Quality
 * Dashboard that summarises the day's signals in plain English, plus 1–3
 * bullets. Server-side advisory; always labelled "AI advisory" with a
 * confidence disclaimer (Annex 22).
 *
 * Source field tells the user whether the response came from a real LLM
 * (Gemini / Anthropic) or the deterministic rule-based fallback.
 */

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../api/client';

interface DashboardAdvisory {
  summary: string;
  concerns: string[];
  advisory_only: boolean;
  source: 'gemini' | 'anthropic' | 'rule-based';
  model_version?: string;
  computed_at_utc: string;
}

export function AiAdvisoryChip() {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ['quality-dashboard', 'ai-advisory'],
    queryFn: ({ signal }) => apiFetch<DashboardAdvisory>('/api/v1/ai/dashboard-advisory', { signal }),
    staleTime: 5 * 60 * 1000,
  });

  if (q.isLoading || q.isError || !q.data) return null;
  const d = q.data;

  // The advisory tokens (`--color-ai-advisory-*`) have light + dark variants
  // already defined in design-system/tokens/colors.css. Inline text colour
  // tokens are also theme-aware. Adding an `aiAdvisoryText` reference here
  // means the summary + bullets stay legible in both themes (where the
  // earlier inline-style version inherited `--color-text-primary` which
  // ALSO themes correctly — the prior bug was light grey on light purple).
  return (
    <section aria-label="AI advisory" className="qd-ai-advisory" style={{
      padding: '14px 16px', borderRadius: 14,
      background: 'var(--color-ai-advisory-bg, #FAF5FF)',
      border: '1px solid var(--color-ai-advisory-border, #E9D5FF)',
      display: 'flex', gap: 14, alignItems: 'flex-start',
    }}>
      <style>{`
        .qd-ai-advisory .qd-ai-summary    { color: var(--color-ai-advisory, #4C1D95); }
        .qd-ai-advisory .qd-ai-bullets li { color: var(--color-text-primary, #111827); }
        /* The design-system explicit-theme block doesn't redefine the AI
           advisory tokens; override them locally so the chip stays
           legible in dark mode. */
        :root[data-theme="dark"] .qd-ai-advisory          { background: rgba(124, 58, 237, 0.18); border-color: #6D28D9; }
        :root[data-theme="dark"] .qd-ai-advisory .qd-ai-summary    { color: #E9D5FF; }
        :root[data-theme="dark"] .qd-ai-advisory .qd-ai-bullets li { color: #F9FAFB; }
        :root[data-theme="dark"] .qd-ai-advisory .qd-ai-disclaimer { color: #C4B5FD; }
        :root[data-theme="dark"] .qd-ai-advisory .qd-ai-pill       { background: #7C3AED; color: #fff; }
      `}</style>
      <div aria-hidden className="qd-ai-pill" style={{
        flexShrink: 0, padding: '4px 10px', borderRadius: 9999,
        background: 'var(--color-ai-advisory, #7C3AED)',
        color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: 0.5, lineHeight: 1.4,
      }}>
        ✨ {t('qualityDashboard.aiAdvisory.label', { defaultValue: 'AI ADVISORY' })}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p className="qd-ai-summary" style={{ margin: 0, fontSize: 14, lineHeight: 1.45, fontWeight: 600 }}>
          {d.summary}
        </p>
        {d.concerns.length > 0 && (
          <ul className="qd-ai-bullets" style={{ margin: '8px 0 0', paddingInlineStart: 20, fontSize: 13, lineHeight: 1.55 }}>
            {d.concerns.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        )}
        <p className="qd-ai-disclaimer" style={{
          margin: '10px 0 0', fontSize: 11, fontStyle: 'italic',
          color: 'var(--color-text-tertiary, #6B7989)',
        }}>
          {t('qualityDashboard.aiAdvisory.disclaimer', {
            defaultValue:
              'AI-generated and advisory only — verify against the source records before relying on it. Per Annex 22, this never triggers an approval.',
          })}
          {d.source !== 'rule-based' && (
            <> &middot; <span style={{ fontFamily: 'monospace' }}>Cloud AI</span></>
          )}
        </p>
      </div>
    </section>
  );
}
